//@flow
import React from 'react';
import ConfirmDialog from "../../../../../components/app/dialogs/confirmDialog/confirmDialog";
import FileUpload from "../../../../../components/form/fileUpload/fileUpload";
import {
	Box,
	Button,
	Checkbox,
	DialogContentText,
	FormControl,
	FormControlLabel,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { Trans, useTranslation } from "react-i18next";
import { dialogueContextTextDefaults } from "../../../../../helpers/theme/defaultProps";
import useExhibitionAction from "../../../../../hooks/exhibitions/useExhibitionAction";
import useExhibitionData from "../../../../../hooks/exhibitions/useExhibitionData";
import BulkImportStatus from "../../../../../components/app/alerts/bulkImportStatus/bulkImportStatus";
import { EXHIBITOR_PROFILE } from "../../../../../locales/components/namespaces";
import { useHasPermission } from "../../../../../modules/permission/hooks/useHasPermission";
import { ACCEPT_EXCEL, API, Permissions } from "../../../../../helpers/constants";
import { AuthenticatedLink } from "../../../../../components/general/authenticatedLink/authenticatedLink";
import ExcelTemplate from "./excellTemplates/excelTemplate";

type Props = {
	exhibitionId: string,
	exhibitorId?: string,
	show: boolean,
	onClose: Function,
	limitedView?: boolean,
	isShortOrganizerImport?: boolean
}

const ExcelImport = (props: Props) => {
	const {
		show,
		onClose,
		exhibitionId,
		exhibitorId,
		limitedView,
		preregistrationNeedsApproval,
		visitorCategoryRequiredInImport,
		isShortOrganizerImport
	} = props
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { createBulkVisitors, clearBulkVisitorStatus, createBulkVisitorsForBadges } = useExhibitionAction()
	const { bulkImporting, visitorBulkImportStatus } = useExhibitionData()
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	
	const onSubmit = (values, { resetForm }) => {
		if (isShortOrganizerImport) {
			createBulkVisitorsForBadges(exhibitionId, values)
		} else {
			createBulkVisitors(exhibitionId, exhibitorId, values)
		}
		resetForm()
	}
	const validationSchema = yup.object({
		files: yup
		.array()
		.min(1)
		.required(t("at least one item needs to be here"))
	})
	const formik = useFormik({
		initialValues: {
			files: [],
			visitorApproved: false
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	const onFileChange = (val) => {
		formik.setFieldValue('files', val)
	}
	
	const isExhibitorTemplate = !isOrganizer || (isOrganizer && exhibitorId && preregistrationNeedsApproval && !formik.values.visitorApproved)
	let url = isExhibitorTemplate ? "/en/visitor/export" :
		isShortOrganizerImport ? "/en/visitor/export-organizer-short" : "/en/visitor/export-organizer"
	const closeDialogue = () => {
		formik.setFieldValue('files', [])
		onClose()
	}
	return (
		<Box>
			<ConfirmDialog
				maxWidth={"md"}
				loading={bulkImporting}
				open={show}
				handleDialog={closeDialogue}
				title={t("Import from excel")}
				otherDialogProps={{ keepMounted: true }}
				yesBtnDisabled={!(formik.isValid && formik.dirty)}
				yesBtnText={t('Import selected file')}
				onNo={closeDialogue}
				onYes={formik.handleSubmit}
				yesBtnVariant={'outlined'}
				hasBtnIcons={false}
			>
				<FormikProvider value={formik}>
					<Box
						sx={{
							'& .MuiTextField-root': { my: 1 },
						}}
						component="form"
						onSubmit={formik.handleSubmit}
					>

							<BulkImportStatus
								onClose={clearBulkVisitorStatus}
								status={visitorBulkImportStatus}
							/>
							<DialogContentText
								{...dialogueContextTextDefaults}
							>
								<Trans
									i18nKey={'import_excel_msg'}
									ns={EXHIBITOR_PROFILE}
								>
									If you want to add more than one people quickly you can upload
									an <strong>Excel</strong> file.<br/>
									<ul>
										<li>Your excel file should follow the structure described bellow.</li>
										<li>Make sure your columns are in order as in the example table.</li>
										<li>For your convenience, you you also download our blank excel template.</li>
									</ul>
								</Trans>
							</DialogContentText>
							<ExcelTemplate
								isExhibitorTemplate={isExhibitorTemplate}
								limitedView={limitedView}
								visitorCategoryRequiredInImport={visitorCategoryRequiredInImport}
								isShortOrganizerImport={isShortOrganizerImport}
							/>
							{exhibitorId && isOrganizer && preregistrationNeedsApproval && (
								<FormControl variant="standard" fullWidth>
									<FormControlLabel
										control={
											<Checkbox
												checked={formik.values.visitorApproved}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												size={'small'}
												name={'visitorApproved'}
											/>
										}
										label={t('Visitor Approved')}
										labelPlacement="end"
									/>
								</FormControl>
							)}
							<AuthenticatedLink
								url={API + url}
								filename={t('Download excel template')}
							>
								<Box sx={{
									width: '100%',
									display: 'flex',
									justifyContent: { xs: 'center', sm: 'center', md: 'end', lg: 'end' }
								}}>
									<Button
										sx={{ marginBottom: 3 }}
										size={"small"}
										component={"button"}
										target={"_blank"}
										color={'highlight'}
									>
										{t('Download excel template')}
									</Button>
								</Box>
							</AuthenticatedLink>
							<FileUpload
								accept={{ [ACCEPT_EXCEL]: ['.xlsx'] }}
								multiple={false}
								onRemove={onFileChange}
								onFilesUpload={onFileChange}
							/>
					</Box>
				</FormikProvider>
			</ConfirmDialog>
		</Box>
	)
}

export default ExcelImport;